import { CompactUserResponse, UserId, VeryCompactUserResponse } from '@business/entities/account';
import { CompactContactResponse, ContactActivityType, ContactId } from '@business/entities/contact';
import { CompactDealBoardResponse, DealBoardId } from '@business/entities/deal';
import { CompactDealStageResponse, DealStageId } from '@business/entities/deal/DealStageResponse';
import { CompactOrgResponse, OrgExtra, OrgId } from '@business/entities/organization';
import { CustomFieldResponseType } from '../integration';
import { ProposalStatus } from '../proposal';
import { BaseResponse } from '../common';

export enum HasDeal {
  all = 'all',
  yes = 'yes',
  no = 'no',
}

export enum DealStatus {
  inProgress = 'in_progress',
  won = 'won',
  lost = 'lost',
  deleted = 'deleted',
  reopen = 'reopen',
}

export enum DealAlertStatus {
  success = 'success',
  warning = 'warning',
  danger = 'danger',
}

export enum SortByOption {
  default = 'default',
  created_date_new_to_old = 'created_date_new_to_old',
  created_date_old_to_new = 'created_date_old_to_new',
  last_activity_new_to_old = 'last_activity_new_to_old',
  last_activity_old_to_new = 'last_activity_old_to_new',
  event_old_to_new = 'event_old_to_new',
  event_new_to_old = 'event_new_to_old',
}

export type DealId = string;

export interface CompactDealResponse extends BaseResponse {
  id: DealId;
  name: string;
  value: number;
  valueDefault: number;
  interval: string;
  status: DealStatus;
  lostExplain: string;
  expectedClosed: number;
  lastActivity: number;
  menuOrder: number;
  orgId: OrgId;
  orgName: string;
  owner: VeryCompactUserResponse;
  isWarning: boolean;
  alertStatus: DealAlertStatus;
  alertMessage: string;
  extra?: OrgExtra;
  stageId: DealStageId;
  stageTitle: string;
  orgCurrency: string;
  noValue: boolean;
  contact?: CompactContactResponse;
  dealAge: string;
  dealAgeToolip: string;
  createdBy: VeryCompactUserResponse;
  timeFrameAlertMessage: string;
  proposalsShort: { id: string; title: string; status: ProposalStatus }[];
  noActivityAfterCreation: boolean;
  upcomingEventDate?: number;
}

export enum DealIntervalResponse {
  onetime = 'one time',
  monthly = 'monthly',
  quarterly = 'quarterly',
  yearly = 'yearly',
}

export type DealRequest = Pick<CompactDealResponse, 'name' | 'value' | 'expectedClosed' | 'interval'> & {
  contactId?: ContactId;
  orgId?: OrgId;
  ownerId?: UserId;
  boardId?: DealBoardId;
  stageId?: DealStageId;
  customFields?: { [key: string]: CustomFieldResponseType };
};

export interface DealResponse extends CompactDealResponse {
  stage: CompactDealStageResponse;
  lostReason: string;
  lostExplain: string;
  contact: CompactContactResponse;
  organization: CompactOrgResponse;
  board: CompactDealBoardResponse;
  logs: DealLogResponse[];
  nextId: DealId;
  customFields: { [key: string]: CustomFieldResponseType };
  proposals: DealProposalResponse[];
}

export interface DealLogResponse {
  id: string;
  value: string;
  note: string;
  user: CompactUserResponse;
  createdAt: number;
}
export type DealOrderRequest = {
  stageId: DealStageId;
  deals: DealId[];
  sortByOptions: SortByOption;
};

export interface DealProposalResponse {
  id: string;
  title: string;
  orgId: string;
  status: ProposalStatus;
  amount: number;
  createdBy: CompactUserResponse;
  created: number;
  image: string;
  proposalTotals: {
    monthly: number[];
    oneTime: number[];
    quarterly: number[];
    yearly: number[];
    oneTimeTotal: number;
    monthlyTotal: number;
    quarterlyTotal: number;
    yearlyTotal: number;
  };
}

export interface DealActivityResponse {
  hasActivity: boolean;
  activities: {
    activityType: ContactActivityType;
    content: string;
    createdAt: number;
  }[];
}
